import React from "react";
import { Space } from "antd";
import { useOutletContext } from "react-router-dom";

import { Value } from "@components/Text";
import { hasAccess } from "@components/Authorization";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { ago, getFormattedTimezone } from "@components/Date";
import { isExternalInvestorPortalEnabled } from "@components/Organization";
import Identity, { TwoFactorAuthenticationSwitch } from "@components/Identity";

import useInvestorAccreditation from "./useInvestorAccreditation.js";

const LABEL_NO = "No";
const LABEL_YES = "Yes";
const LABEL_NEVER = "Never";
const LABEL_CREATED = "Created";
const LABEL_TIMEZONE = "Time Zone";
const LABEL_REFERRED = "Referred From";
const LABEL_SUBSCRIBED = "Subscribed to Campaigns";
const LABEL_LAST_SIGNED_IN = "Last Signed In";
const LABEL_LAST_FUNDED_AT = "Last Invested Date";

type Investor = {
  email?: string;
  address?: Record<string, unknown>;
  givenName?: string;
  familyName?: string;
  dateOfBirth?: string;
  mobileNumber?: string;
  timeZoneCode?: string;
  additionalName?: string;
  campaignEmails?: string[];
  alternativeName?: string;
  hasAccreditation?: boolean;
  alternativeEmail?: string;
  accreditationExpiresAt?: string;
};

type Account = {
  id: string;
  investor: Investor;
  sponsorId?: string;
  createdAt?: string;
  createdBy?: string;
  isDisabled?: boolean;
  is2FaEnabled?: boolean;
  brokerUserId?: string;
  lastFundedAt?: string;
  disableReason?: string;
  correlationId?: string;
  referralSource?: string;
  lastAuthenticatedAt?: string;
  isSubscribedForCampaigns?: boolean;
};

type DescriptionItem = {
  label: React.ReactNode;
  value: React.ReactNode;
};

type OutletContext = {
  getSponsorName: (sponsorId?: string) => string;
  hasMultipleSponsors: boolean;
};

const useItems = (
  account: Account,
  onAfterUpdated: (updatedAccount: Account) => void,
): DescriptionItem[] => {
  const {
    getSponsorName,
    hasMultipleSponsors,
  } = useOutletContext<OutletContext>();

  const { getOrganizationId } = useAppContext() as AppContext;

  const canUpdateAccount = hasAccess(["accounts-write"]);

  const organizationId = getOrganizationId();

  // NOTE: an external identity provider holds the investor identities of these
  //       organizations, and it also does their two-factor authentication.
  const isExternalPortalEnabled = isExternalInvestorPortalEnabled(organizationId);

  const {
    id: accountId,
    investor,
    sponsorId,
    createdAt,
    createdBy,
    isDisabled,
    brokerUserId,
    lastFundedAt,
    disableReason,
    correlationId,
    referralSource,
    isSubscribedForCampaigns,
  } = account;

  const sponsorName = getSponsorName(sponsorId);

  const {
    email,
    address,
    givenName,
    familyName,
    dateOfBirth,
    mobileNumber,
    timeZoneCode,
    additionalName,
    alternativeName,
    alternativeEmail,
    hasAccreditation,
    accreditationExpiresAt,
    campaignEmails = [],
  } = investor;

  const hasInvestorAccreditation = useInvestorAccreditation(hasAccreditation, accreditationExpiresAt);

  const shouldInclude2Fa =
    !!mobileNumber &&
    !!correlationId &&
    canUpdateAccount;

  const legalFullName = [
    givenName,
    additionalName,
    familyName,
  ].join(" ");

  const onSuccessSwitch2FA = (is2FaEnabled: boolean): void =>
    onAfterUpdated({ ...account, is2FaEnabled });

  const liaisonItems: DescriptionItem[] = [
    {
      label: "Liaison",
      value: brokerUserId && <Identity id={brokerUserId} />,
    },
  ];

  if (hasMultipleSponsors) {
    liaisonItems.push(
      {
        label: "Sponsor",
        value: sponsorName,
      },
    );
  }

  const items: DescriptionItem[] = [
    {
      label: "Legal Full Name",
      value: legalFullName,
    },
    {
      label: "Preferred Name",
      value: alternativeName,
    },
    {
      label: "Date of Birth",
      value: dateOfBirth && <Value value={dateOfBirth} />,
    },
    {
      label: "Email",
      value: email,
    },
    {
      label: "Alternative Email",
      value: alternativeEmail,
    },
    {
      label: "Campaign Emails",
      value: campaignEmails.join(", "),
    },
    {
      label: LABEL_SUBSCRIBED,
      value: isSubscribedForCampaigns ? LABEL_YES : LABEL_NO,
    },
    {
      label: "Phone Number",
      value: mobileNumber && <Value value={mobileNumber} />,
    },
  ];

  if (shouldInclude2Fa) {
    const { is2FaEnabled = false } = account;

    items.push({
      label: "Two Factor Authentication",
      value: (
        <TwoFactorAuthenticationSwitch
          accountId={accountId}
          isEnabled={is2FaEnabled}
          onSuccess={onSuccessSwitch2FA}
          isDisabled={isExternalPortalEnabled}
        />
      ),
    });
  }

  items.push(
    {
      label: "Mailing Address",
      value: address && <Value value={address} />,
    },
    {
      label: LABEL_TIMEZONE,
      value: getFormattedTimezone(timeZoneCode),
    },
    {
      label: "Accredited Investor",
      value: hasInvestorAccreditation,
    },
    ...liaisonItems,
    {
      label: LABEL_REFERRED,
      value: referralSource,
    },
    {
      label: LABEL_CREATED,
      value: (
        <Space>
          <Value value={createdAt} />
          { createdBy && <>by <Identity id={createdBy} /></> }
        </Space>
      ),
    },
    {
      label: LABEL_LAST_SIGNED_IN,
      value: ago(account.lastAuthenticatedAt) || LABEL_NEVER,
    },
    {
      label: LABEL_LAST_FUNDED_AT,
      value: lastFundedAt
        ? <Value value={lastFundedAt} />
        : LABEL_NEVER,
    },
  );

  const shouldShowDisableReason = isDisabled && disableReason;

  if (shouldShowDisableReason) {
    items.push(
      {
        label: "Disable Reason",
        value: disableReason,
      },
    );
  }

  return items;
};

export default useItems;
